import { RefObject, useEffect } from 'react';
import { useClickOutside } from '../../../hooks/use-click-outside';
import { useKeyDown } from '../../../hooks/use-key-down';

interface Props {
  offset?: number;
  position?: 'top' | 'right' | 'bottom' | 'left';
  triggerRef: RefObject<HTMLElement>;
  tooltipRef: RefObject<HTMLElement>;
  onClose: () => void;
  behavior?: 'click' | 'hover';
  isOpen?: boolean;
}

export const useTooltipPortal = (props: Props) => {
  const {
    offset = 0,
    position = 'bottom',
    tooltipRef,
    triggerRef,
    onClose,
    behavior,
    isOpen = true,
  } = props;

  useKeyDown(triggerRef, 'Escape', onClose);
  useClickOutside(triggerRef, onClose);

  useEffect(() => {
    if (!isOpen) return;

    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    tooltip.style.position = 'fixed';
    tooltip.style.zIndex = '1001';
    if (behavior === 'click') {
      tooltip.style.minWidth = `${trigger.clientWidth}px`;
    }

    requestAnimationFrame(() => {
      const triggerRect = trigger.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      let top = 0;
      let left = 0;

      if (position === 'bottom') {
        top = triggerRect.top + triggerRect.height + offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      } else if (position === 'top') {
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      } else if (position === 'right') {
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + offset;
      } else if (position === 'left') {
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - offset;
      }

      const padding = 8;

      if (left < padding) {
        left = padding;
      } else if (left + tooltipRect.width > window.innerWidth - padding) {
        left = window.innerWidth - tooltipRect.width - padding;
      }

      if (top < padding) {
        top = padding;
      } else if (top + tooltipRect.height > window.innerHeight - padding) {
        top = window.innerHeight - tooltipRect.height - padding;
      }

      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
    });
  }, [offset, isOpen, position, tooltipRef, triggerRef]);
};
